import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Database, UploadCloud, GitBranch, ShieldCheck, Sparkles, LineChart,
  ClipboardCheck, Users, Layers, PlayCircle, CheckCircle2, XCircle,
  RotateCcw, Loader2, Plus, FileScan, MapPin,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Migration Overview", icon: Database },
  { key: "imports", label: "Bulk Import", icon: UploadCloud },
  { key: "workflow", label: "Progressive Workflow", icon: GitBranch },
  { key: "validation", label: "AI Validation", icon: ShieldCheck },
  { key: "extraction", label: "Survey Extraction", icon: FileScan },
  { key: "quality", label: "Data Quality", icon: ClipboardCheck },
  { key: "productivity", label: "Productivity", icon: Users },
  { key: "forecast", label: "Forecasting", icon: LineChart },
];

const STAGE_COLORS = {
  imported: "cad-chip-blue",
  ai_validation: "cad-chip-blue",
  gis_validation: "cad-chip-blue",
  survey_validation: "cad-chip-blue",
  legal_validation: "cad-chip-blue",
  quality_assurance: "cad-chip-gold",
  supervisor_review: "cad-chip-gold",
  registrar_approval: "cad-chip-gold",
  published: "cad-chip-green",
};

const KPI = ({ label, value, hint }) => (
  <div className="cad-panel p-4">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{value}</div>
    {hint && <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">{hint}</div>}
  </div>
);

const SAMPLE_EXTRACT = `IN THE MATTER of Cadastral Diagram SG 2841/1996. Parcel LOT-4127/34 in favour of Chirandu Family Trust for Stand 4127 Harare Township measuring 823 square metres, registered on 14th of March 1978. Beacon references BM-A1, BM-A2, SL-14. Surveyor: T. Chikwanha.`;

export const MigrationCentre = () => {
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [extractText, setExtractText] = useState(SAMPLE_EXTRACT);
  const [extractResult, setExtractResult] = useState(null);
  const [newBatch, setNewBatch] = useState({
    project_id: "", source_format: "geojson",
    file_name: "national_cadastre_import.geojson", province: "Harare", district: "",
  });

  const ref = useQuery({ queryKey: ["mig-ref"], queryFn: async () => (await api.get("/migration/reference")).data });
  const projects = useQuery({ queryKey: ["mig-projects"], queryFn: async () => (await api.get("/migration/projects")).data });
  const dashboard = useQuery({ queryKey: ["mig-dash"], queryFn: async () => (await api.get("/migration/dashboard")).data });
  const quality = useQuery({ queryKey: ["mig-quality"], queryFn: async () => (await api.get("/migration/quality")).data });
  const productivity = useQuery({ queryKey: ["mig-prod"], queryFn: async () => (await api.get("/migration/productivity")).data });
  const forecast = useQuery({ queryKey: ["mig-forecast"], queryFn: async () => (await api.get("/migration/forecast")).data });
  const batches = useQuery({ queryKey: ["mig-batches"], queryFn: async () => (await api.get("/migration/batches", { params: { limit: 40 } })).data });

  const [selectedBatch, setSelectedBatch] = useState(null);
  const records = useQuery({
    queryKey: ["mig-records", selectedBatch],
    queryFn: async () => (await api.get(`/migration/batches/${selectedBatch}/records`, { params: { limit: 30 } })).data,
    enabled: !!selectedBatch,
  });

  const createBatch = useMutation({
    mutationFn: async () => (await api.post("/migration/batches", newBatch)).data,
    onSuccess: () => {
      toast.success("Batch staged for validation");
      qc.invalidateQueries({ queryKey: ["mig-batches"] });
      qc.invalidateQueries({ queryKey: ["mig-dash"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const runValidate = async (bid) => {
    setBusy(true);
    try {
      await api.post(`/migration/batches/${bid}/validate`);
      toast.success("AI validation pass complete");
      qc.invalidateQueries({ queryKey: ["mig-batches"] });
      qc.invalidateQueries({ queryKey: ["mig-records", bid] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const approve = async (bid) => {
    try { await api.post(`/migration/batches/${bid}/approve`); toast.success("Batch approved"); qc.invalidateQueries({ queryKey: ["mig-batches"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const publish = async (bid) => {
    try { await api.post(`/migration/batches/${bid}/publish`); toast.success("Batch published to national cadastre"); qc.invalidateQueries({ queryKey: ["mig-batches"] }); qc.invalidateQueries({ queryKey: ["mig-dash"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rollback = async (bid) => {
    try { await api.post(`/migration/batches/${bid}/rollback`); toast.success("Batch rolled back to imported"); qc.invalidateQueries({ queryKey: ["mig-batches"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const advance = async (rid) => {
    try { await api.post(`/migration/records/${rid}/advance`, {}); qc.invalidateQueries({ queryKey: ["mig-records", selectedBatch] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const runExtract = async () => {
    setBusy(true); setExtractResult(null);
    try {
      const { data } = await api.post("/migration/extraction", { text: extractText, document_type: "survey_diagram" });
      setExtractResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const dash = dashboard.data;
  const q = quality.data;
  const prod = productivity.data;
  const fc = forecast.data;

  return (
    <div className="space-y-6" data-testid="page-migration-centre">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">National Land Data Migration & Acceleration Platform</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Database className="text-[#C99A2E]" /> National Data Migration Centre
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Ingest, validate, enrich and publish cadastral information at national scale. Every imported record moves through a nine-stage
          progressive workflow with full audit trail. Imported data is isolated until formally approved by the Registrar.
        </div>
      </header>

      {/* Tab strip */}
      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1" data-testid="migration-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`mig-tab-${t.key}`}
            className={`px-3 py-2 rounded-sm text-[12px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-2
                        ${tab === t.key ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25"}`}>
            <t.icon size={13} className="text-[#C99A2E]" /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="space-y-4" data-testid="mig-overview">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPI label="Batches" value={dash?.total_batches ?? "—"} />
            <KPI label="Total Records" value={(dash?.total_records ?? 0).toLocaleString()} />
            <KPI label="Published" value={(dash?.published ?? 0).toLocaleString()} hint={`${dash?.completion_pct ?? 0}% complete`} />
            <KPI label="In Pipeline" value={(dash?.in_pipeline ?? 0).toLocaleString()} />
            <KPI label="Low-Quality Flagged" value={(dash?.flagged_low_quality ?? 0).toLocaleString()} />
            <KPI label="Projected ETA" value={fc?.estimated_completion ?? "—"} hint={`${fc?.weeks_remaining ?? 0} weeks remaining`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cad-panel p-5">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Stage distribution</div>
              <div className="space-y-2">
                {(dash?.stages || []).map((s) => (
                  <div key={s.stage} className="flex items-center gap-3">
                    <div className="w-40 text-[12px] text-[#DCE3E8] font-mono-cad">{s.label}</div>
                    <div className="flex-1 h-2 bg-[#18232D] rounded-full overflow-hidden">
                      <div className="h-full bg-[#C99A2E]" style={{ width: `${Math.min(100, (s.count / (dash?.total_records || 1)) * 100 * 4)}%` }} />
                    </div>
                    <div className="w-16 text-right font-mono-cad text-[12px] text-[#F6F8FA]">{s.count.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cad-panel p-5">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Province ranking</div>
              <table className="w-full text-[12.5px]">
                <thead><tr className="text-left border-b cad-hairline">
                  {["Province", "Total", "Published", "Rate"].map((h) => (
                    <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="cad-tbody">
                  {(dash?.province_ranking || []).map((r) => (
                    <tr key={r.province} data-testid={`mig-rank-${r.province.replace(/\s+/g, "-")}`}>
                      <td className="px-2 py-1.5 text-[#F6F8FA] flex items-center gap-1.5"><MapPin size={11} className="text-[#C99A2E]" />{r.province}</td>
                      <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.total.toLocaleString()}</td>
                      <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.published.toLocaleString()}</td>
                      <td className="px-2 py-1.5 font-mono-cad text-[#6BB88A]">{r.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Active migration projects</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(projects.data || []).map((p) => (
                <div key={p.id} className="border cad-hairline rounded-sm p-4 bg-[#071B2B]" data-testid={`mig-project-${p.code}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-[#F6F8FA] text-[15px]">{p.name}</div>
                    <span className={`cad-chip ${p.status === "completed" ? "cad-chip-green" : "cad-chip-gold"}`}>{p.status}</span>
                  </div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E] mt-1">{p.code}</div>
                  <div className="text-[12px] text-[#B3C2CC] mt-2 leading-relaxed">{p.description}</div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] font-mono-cad">
                    <div><div className="text-[#7C93A5] uppercase tracking-widest text-[9px]">Target</div><div className="text-[#F6F8FA]">{p.target_records.toLocaleString()}</div></div>
                    <div><div className="text-[#7C93A5] uppercase tracking-widest text-[9px]">Deadline</div><div className="text-[#F6F8FA]">{p.deadline}</div></div>
                    <div><div className="text-[#7C93A5] uppercase tracking-widest text-[9px]">Lead</div><div className="text-[#F6F8FA] truncate">{p.lead_officer}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import */}
      {tab === "imports" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="mig-imports">
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Bulk import batches</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">All imports are staged in isolation. Nothing enters the national cadastre without registrar approval and publication.</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Batch", "Format", "Province", "Records", "Quality", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(batches.data || []).map((b) => (
                  <tr key={b.id} data-testid={`mig-batch-${b.id}`}>
                    <td className="px-2 py-1.5 font-mono-cad text-[#F6F8FA]">{b.batch_no}</td>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-blue text-[10px] uppercase">{b.source_format}</span></td>
                    <td className="px-2 py-1.5 text-[#DCE3E8]">{b.province}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{b.total_records.toLocaleString()}</td>
                    <td className="px-2 py-1.5 font-mono-cad">
                      <span className={b.quality_score >= 85 ? "text-[#6BB88A]" : b.quality_score >= 65 ? "text-[#C99A2E]" : "text-[#E67E7E]"}>{b.quality_score}</span>
                    </td>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[10px] uppercase">{b.status.replace(/_/g, " ")}</span></td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        <button onClick={() => setSelectedBatch(b.id)} data-testid={`mig-inspect-${b.id}`}
                                className="cad-btn-outline px-2 py-1 text-[10px] rounded-sm">Inspect</button>
                        <button onClick={() => runValidate(b.id)} disabled={busy} data-testid={`mig-validate-${b.id}`}
                                className="cad-btn-outline px-2 py-1 text-[10px] rounded-sm inline-flex items-center gap-1">
                          {busy ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} Validate
                        </button>
                        <button onClick={() => approve(b.id)} data-testid={`mig-approve-${b.id}`}
                                className="cad-btn-outline px-2 py-1 text-[10px] rounded-sm inline-flex items-center gap-1"><CheckCircle2 size={10} /> Approve</button>
                        <button onClick={() => publish(b.id)} data-testid={`mig-publish-${b.id}`}
                                className="cad-btn-primary px-2 py-1 text-[10px] rounded-sm inline-flex items-center gap-1"><PlayCircle size={10} /> Publish</button>
                        <button onClick={() => rollback(b.id)} data-testid={`mig-rollback-${b.id}`}
                                className="cad-btn-outline px-2 py-1 text-[10px] rounded-sm inline-flex items-center gap-1"><RotateCcw size={10} /> Rollback</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">New bulk import</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Simulated ingest — a real deployment binds to GDAL for full format parsing.</div>
            <div className="space-y-2 mt-3">
              <select value={newBatch.project_id} onChange={(e) => setNewBatch({ ...newBatch, project_id: e.target.value })}
                      data-testid="mig-new-project"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                <option value="">Select migration project…</option>
                {(projects.data || []).map((p) => (<option key={p.id} value={p.id}>{p.code} — {p.name}</option>))}
              </select>
              <select value={newBatch.source_format} onChange={(e) => setNewBatch({ ...newBatch, source_format: e.target.value })}
                      data-testid="mig-new-format"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.formats || []).map((f) => (<option key={f} value={f}>{f.toUpperCase().replace(/_/g, " ")}</option>))}
              </select>
              <select value={newBatch.province} onChange={(e) => setNewBatch({ ...newBatch, province: e.target.value })}
                      data-testid="mig-new-province"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.provinces || []).map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
              <input value={newBatch.file_name} onChange={(e) => setNewBatch({ ...newBatch, file_name: e.target.value })}
                     placeholder="Source file name" data-testid="mig-new-file"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
              <button onClick={() => createBatch.mutate()} disabled={!newBatch.project_id || createBatch.isPending}
                      data-testid="mig-new-submit"
                      className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
                {createBatch.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Stage batch
              </button>
            </div>
            <div className="mt-4 border-t cad-hairline pt-3">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Supported formats</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(ref.data?.formats || []).map((f) => (<span key={f} className="cad-chip cad-chip-blue text-[10px] uppercase">{f}</span>))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workflow */}
      {tab === "workflow" && (
        <div className="cad-panel p-5" data-testid="mig-workflow">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Progressive migration workflow</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Nine controlled stages with immutable audit trail. Nothing bypasses approval.</div>

          <div className="flex flex-wrap gap-2 mt-4">
            {(ref.data?.stages || []).map((s, i) => (
              <div key={s.key} className="border cad-hairline rounded-sm px-3 py-2 min-w-[150px] bg-[#071B2B]" data-testid={`mig-stage-${s.key}`}>
                <div className="font-mono-cad text-[9px] uppercase tracking-widest text-[#7C93A5]">Stage {i + 1}</div>
                <div className="text-[#F6F8FA] font-display font-bold text-[13px]">{s.label}</div>
                <div className="text-[10px] font-mono-cad text-[#C99A2E] mt-1">
                  {(dash?.stages || []).find((x) => x.stage === s.key)?.count?.toLocaleString() || 0} records
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 border-t cad-hairline pt-4">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Batch inspection {selectedBatch ? `— ${selectedBatch.slice(-6)}` : "— pick a batch under Bulk Import"}</div>
            {selectedBatch && (
              <table className="w-full mt-3 text-[12px]">
                <thead><tr className="text-left border-b cad-hairline">
                  {["Parcel", "Owner", "Province", "Stage", "Quality", "Issues", "Action"].map((h) => (
                    <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="cad-tbody">
                  {(records.data || []).map((r) => (
                    <tr key={r.id} data-testid={`mig-record-${r.id}`}>
                      <td className="px-2 py-1.5 font-mono-cad text-[#F6F8FA]">{r.parcel_number}</td>
                      <td className="px-2 py-1.5 text-[#DCE3E8]">{r.owner}</td>
                      <td className="px-2 py-1.5 text-[#B3C2CC]">{r.province}</td>
                      <td className="px-2 py-1.5"><span className={`cad-chip ${STAGE_COLORS[r.stage] || "cad-chip-blue"} text-[10px] uppercase`}>{r.stage.replace(/_/g, " ")}</span></td>
                      <td className={`px-2 py-1.5 font-mono-cad ${r.quality_score >= 85 ? "text-[#6BB88A]" : r.quality_score >= 65 ? "text-[#C99A2E]" : "text-[#E67E7E]"}`}>{r.quality_score}</td>
                      <td className="px-2 py-1.5 text-[11px] text-[#B3C2CC]">{(r.quality_issues || []).slice(0, 2).join(", ") || "—"}</td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => advance(r.id)} data-testid={`mig-advance-${r.id}`}
                                className="cad-btn-outline px-2 py-1 text-[10px] rounded-sm">Advance →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* AI Validation */}
      {tab === "validation" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mig-validation">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">AI validation checks</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Every imported record is swept for coordinate integrity, topology and metadata completeness.</div>
            <ul className="mt-3 space-y-1.5 text-[12.5px] text-[#DCE3E8]">
              {["Coordinate format & precision", "CRS consistency & projection detection", "Polygon closure & orientation",
                "Duplicate parcel detection", "Overlap & gap detection", "Sliver polygon detection", "Self-intersection detection",
                "Beacon reference validity", "Survey tolerance compliance", "Minimum / maximum parcel size", "Owner completeness",
                "Legal encumbrance cross-reference"].map((x) => (
                <li key={x} className="flex items-center gap-2"><CheckCircle2 size={12} className="text-[#6BB88A]" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Current validation posture</div>
            <div className="mt-3 space-y-3">
              {(q?.issue_counts || []).slice(0, 8).map((i) => (
                <div key={i.issue}>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-[#F6F8FA]">{i.label}</span>
                    <span className="font-mono-cad text-[#C99A2E]">{i.count.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-[#18232D] rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-[#C99A2E]" style={{ width: `${Math.min(100, (i.count / ((q?.issue_counts?.[0]?.count) || 1)) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Extraction */}
      {tab === "extraction" && (
        <div className="cad-panel p-5" data-testid="mig-extraction">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">AI-assisted survey document extraction</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Officers verify rather than retype. Paste OCR text from a survey diagram, deed or coordinate schedule.</div>
          <textarea value={extractText} onChange={(e) => setExtractText(e.target.value)} rows={6} data-testid="mig-extract-input"
                    className="mt-3 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[12.5px] text-[#F6F8FA]" />
          <button onClick={runExtract} disabled={busy || !extractText.trim()} data-testid="mig-extract-run"
                  className="cad-btn-primary mt-3 px-4 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Extract for verification
          </button>
          {extractResult && (
            <div className="mt-4 border cad-hairline rounded-sm p-4 bg-[#071B2B]" data-testid="mig-extract-result">
              <div className="flex items-center justify-between">
                <span className="cad-chip cad-chip-gold uppercase">{extractResult.document_type.replace(/_/g, " ")}</span>
                <span className="font-mono-cad text-[11px] text-[#7C93A5]">confidence {Math.round(extractResult.confidence * 100)}%</span>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px]">
                {Object.entries(extractResult.extracted).map(([k, v]) => (
                  <div key={k}>
                    <div className="font-mono-cad text-[9px] uppercase tracking-widest text-[#7C93A5]">{k.replace(/_/g, " ")}</div>
                    <div className="text-[#F6F8FA]">{Array.isArray(v) ? (v.join(", ") || "—") : (v || "—")}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-[#B3C2CC] italic mt-3">{extractResult.notes}</div>
            </div>
          )}
        </div>
      )}

      {/* Quality */}
      {tab === "quality" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mig-quality">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Province quality scores</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Province", "Records", "Score"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(q?.province_scores || []).map((r) => (
                  <tr key={r.province}>
                    <td className="px-2 py-1.5 text-[#F6F8FA]">{r.province}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.records.toLocaleString()}</td>
                    <td className={`px-2 py-1.5 font-mono-cad ${r.average_score >= 85 ? "text-[#6BB88A]" : r.average_score >= 65 ? "text-[#C99A2E]" : "text-[#E67E7E]"}`}>{r.average_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">AI-generated recommendations</div>
            <ul className="mt-3 space-y-2 text-[13px] text-[#DCE3E8]">
              {(q?.recommendations || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 leading-relaxed"><Sparkles size={13} className="text-[#C99A2E] mt-0.5" />{r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Productivity */}
      {tab === "productivity" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mig-productivity">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Officer productivity</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Officer", "Advances", "Validated", "Approved", "Published"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(prod?.officers || []).map((o) => (
                  <tr key={o.officer}>
                    <td className="px-2 py-1.5 text-[#F6F8FA] font-mono-cad">{o.officer}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{o.advances}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{o.validated}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#7CBFDC]">{o.approved}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#6BB88A]">{o.published}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Bottleneck analysis</div>
            <div className="space-y-3 mt-3">
              {(prod?.bottlenecks || []).map((b) => (
                <div key={b.stage} className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                  <div className="flex items-center justify-between">
                    <div className="text-[#F6F8FA] font-display font-bold text-[13px]">{b.stage.replace(/_/g, " ")}</div>
                    <span className="cad-chip cad-chip-gold text-[10px] uppercase">{b.backlog.toLocaleString()} pending</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Forecast */}
      {tab === "forecast" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="mig-forecast">
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">National migration forecast</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <KPI label="Projected Completion" value={fc?.estimated_completion ?? "—"} />
              <KPI label="Weeks Remaining" value={fc?.weeks_remaining ?? "—"} />
              <KPI label="Weekly Throughput" value={(fc?.weekly_throughput ?? 0).toLocaleString()} />
              <KPI label="Remaining Records" value={(fc?.remaining_records ?? 0).toLocaleString()} />
              <KPI label="Recommended Staffing" value={fc?.staffing_recommendation ?? "—"} hint="officers" />
            </div>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">High-risk provinces</div>
            <div className="space-y-2 mt-3">
              {(fc?.high_risk_provinces || []).map((r) => (
                <div key={r.province} className="border cad-hairline rounded-sm p-3 bg-[#071B2B]">
                  <div className="text-[#F6F8FA] font-display font-bold text-[13px]">{r.province}</div>
                  <span className="cad-chip cad-chip-red text-[10px] uppercase mt-1 inline-block">{r.risk.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
